import { FC, useEffect, useState } from "react";
import { Button, Form, Input, message, Modal, Row, Space } from "../../../design-system";
import { SpendCategory, UpdateVendorRequest, Vendor } from "../../../types/VendorTypes";
import { getVendorCategories, updateVendor } from "../../../common/api/VendorClient";
import { handleError } from "../../../common/ErrorHandling";
import { CategoryPicker } from "../../../common/components/CategoryPicker";
import { categoryLabel } from "../../../common/components/CategoryPath";
import { SvgIcon } from "../../../common/components/SvgIcon";
import {
    clean,
    FIELD_MAX,
    LogoMode,
    logoModeOf,
    logoUrlFor,
    VendorLogoField,
} from "./VendorLogoField";
import styles from "./VendorUpdateModal.module.less";

export interface VendorUpdateModalProps {
    vendor: Vendor;
    /** Called with the vendor as the server saved it. */
    onSaved: (vendor: Vendor) => void;
    onCancel: () => void;
}

interface VendorUpdateFormValues {
    name: string;
    url?: string;
    customLogo?: string;
}

const NAME_MIN = 2;

/** The path (root first) of the taxonomy leaf with this label, if there is one. */
const leafPath = (
    categories: SpendCategory[],
    label: string,
    parent: string[] = []
): string[] | undefined => {
    for (const category of categories) {
        const path = [...parent, category.label];
        if (category.children?.length) {
            const found = leafPath(category.children, label, path);
            if (found) return found;
        } else if (category.label === label) {
            return path;
        }
    }
    return undefined;
};

/**
 * Super-admin modal to edit a vendor's details. Same fields and logo handling
 * as the create modal, plus the vendor's spend category, picked from the IL
 * taxonomy with the same picker projects use for their category.
 */
export const VendorUpdateModal: FC<VendorUpdateModalProps> = ({ vendor, onSaved, onCancel }) => {
    const [form] = Form.useForm<VendorUpdateFormValues>();
    const [loading, setLoading] = useState(false);
    const [logoMode, setLogoMode] = useState<LogoMode>(logoModeOf(vendor.hero, vendor.url));
    const [categories, setCategories] = useState<SpendCategory[]>();
    // A leaf label of the IL spend taxonomy; undefined when cleared.
    const [subSpendCategory, setSubSpendCategory] = useState(vendor.subSpendCategory);
    const [pickerOpen, setPickerOpen] = useState(false);
    const watchedName = Form.useWatch("name", form);
    const watchedUrl = Form.useWatch("url", form);
    const watchedCustomLogo = Form.useWatch("customLogo", form);

    useEffect(() => {
        getVendorCategories().then(setCategories).catch(handleError);
    }, []);

    const categoryPath =
        categories && subSpendCategory ? leafPath(categories, subSpendCategory) : undefined;
    // Until the taxonomy loads, the leaf alone is what we know.
    const categoryText = categoryPath ? categoryLabel(categoryPath) : subSpendCategory ?? "";

    const pickCategory = (label?: string) => {
        setSubSpendCategory(label);
        setPickerOpen(false);
    };

    const handleSubmit = async (values: VendorUpdateFormValues) => {
        setLoading(true);
        try {
            const request: UpdateVendorRequest = {
                vendorKey: vendor.vendorKey,
                name: values.name.trim(),
                url: clean(values.url) ?? "",
                hero: logoUrlFor(logoMode, values.url, values.customLogo) ?? "",
                subSpendCategory: subSpendCategory ?? null,
            };
            const saved = await updateVendor(request);
            message.success(`Vendor "${saved.name}" updated`);
            onSaved(saved);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Edit vendor"
            open
            footer={null}
            destroyOnClose
            maskClosable={!loading}
            closable={!loading}
            onCancel={onCancel}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    name: vendor.name,
                    url: vendor.url,
                    customLogo: logoModeOf(vendor.hero, vendor.url) === "custom" ? vendor.hero : "",
                }}
                onFinish={handleSubmit}
                disabled={loading}
            >
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[
                        {
                            required: true,
                            whitespace: true,
                            min: NAME_MIN,
                            message: `Enter a vendor name (at least ${NAME_MIN} characters)`,
                        },
                        { max: FIELD_MAX, message: `Keep the name under ${FIELD_MAX} characters` },
                    ]}
                >
                    <Input autoFocus placeholder="Vendor name" />
                </Form.Item>

                <Form.Item
                    label="Website"
                    name="url"
                    rules={[
                        {
                            max: FIELD_MAX,
                            message: `Keep the website under ${FIELD_MAX} characters`,
                        },
                    ]}
                >
                    <Input placeholder="vendor.com" />
                </Form.Item>

                <VendorLogoField
                    vendorName={(watchedName ?? "").trim()}
                    website={watchedUrl}
                    customLogo={watchedCustomLogo}
                    mode={logoMode}
                    onModeChange={setLogoMode}
                />

                <Form.Item label="Spend category" htmlFor="vendor-spend-category">
                    <CategoryPicker
                        categories={categories ?? []}
                        value={categoryPath}
                        onSelect={(path) => pickCategory(path[path.length - 1])}
                        onClear={() => pickCategory(undefined)}
                        clearLabel="Clear category"
                        open={pickerOpen}
                        onOpenChange={setPickerOpen}
                        trigger={
                            <Input
                                id="vendor-spend-category"
                                readOnly
                                className={styles.categoryField}
                                value={categoryText}
                                placeholder="Not categorized"
                                suffix={
                                    <SvgIcon name="down" size={12} className={styles.chevron} />
                                }
                            />
                        }
                    />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                    <Row justify="end">
                        <Space size={16}>
                            <Button onClick={onCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Save
                            </Button>
                        </Space>
                    </Row>
                </Form.Item>
            </Form>
        </Modal>
    );
};
